// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AccountsHoldingsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import type { HistValue } from '@shared/ui-kit';
import { buildTopPositions } from './utils/build-top-positions';

const SCROLL_AMOUNT = 250;

export interface TopPositionsProps {
  positions: AccountsHoldingsData[];
  currency: AccountCurrency;
  activePeriod: HistValue;
}

export const useTopPositions = ({ positions, activePeriod }: TopPositionsProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState({ left: false, right: false });

  const onScrollContainer = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
    setIsOverflowing({
      left: scrollLeft > 0,
      right: scrollLeft + clientWidth < scrollWidth,
    });
  }, []);

  const onScroll = useCallback((direction: 'left' | 'right') => {
    scrollContainerRef.current?.scrollBy({
      left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  }, []);

  const onScrollLeft = useCallback(() => onScroll('left'), [onScroll]);
  const onScrollRight = useCallback(() => onScroll('right'), [onScroll]);

  const topPositions = useMemo(
    () => buildTopPositions(positions, activePeriod),
    [positions, activePeriod],
  );

  useEffect(() => {
    onScrollContainer();
  }, [onScrollContainer, topPositions]);

  useEffect(() => {
    window.addEventListener('resize', onScrollContainer);

    return () => {
      window.removeEventListener('resize', onScrollContainer);
    };
  }, [onScrollContainer]);

  return {
    isOverflowing,
    onScrollContainer,
    onScrollLeft,
    onScrollRight,
    scrollContainerRef,
    topPositions,
  };
};
